class TokenManager {
  /**
   * Store tokens and user session data
   */
  /**
   * Store tokens and user session data
   * @param tokens {object} - { accessToken, refreshToken }
   * @param sessionId {string|null}
   * @param remember {boolean} - if true store in localStorage (persistent), else sessionStorage
   */
  static storeTokens(tokens, sessionId = null, remember = false) {
    const target = remember ? localStorage : sessionStorage;
    if (tokens.accessToken) {
      target.setItem('authToken', tokens.accessToken);
    }
    if (tokens.refreshToken) {
      target.setItem('refreshToken', tokens.refreshToken);
    }
    if (sessionId) {
      target.setItem('sessionId', sessionId);
    }
  }

  /**
   * Get access token
   */
  static getAccessToken() {
    return sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
  }

  /**
   * Get refresh token
   */
  static getRefreshToken() {
    return sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');
  }

  /**
   * Get session ID
   */
  static getSessionId() {
    return sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated() {
    return !!this.getAccessToken();
  }

  /**
   * Clear all tokens and auth data
   */
  static clearTokens() {
    // Remove from both storages
    try {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('sessionId');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      // Also clear related user info
      sessionStorage.removeItem('userInfo');
      sessionStorage.removeItem('employee_code');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('employee_code');
    } catch (err) {
      console.warn('Error clearing tokens:', err);
    }
  }

  /**
   * Get Authorization header value
   */
  static getAuthHeader() {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Parse JWT token (without verification)
   * Note: This is just to extract claims, NOT for security validation
   */
  static parseToken(token = null) {
    try {
      const tokenToParse = token || this.getAccessToken();
      if (!tokenToParse) return null;

      const parts = tokenToParse.split('.');
      if (parts.length !== 3) return null;

      // Decode the payload (second part)
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      return payload;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  /**
   * Check if access token is expired
   */
  static isAccessTokenExpired() {
    const payload = this.parseToken();
    if (!payload || !payload.exp) return true;

    // Convert JWT exp (seconds) to milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();

    // Consider token expired if less than 1 minute remains
    return currentTime >= (expirationTime - 60000);
  }

  /**
   * Get token remaining time in seconds
   */
  static getTokenRemainingTime() {
    const payload = this.parseToken();
    if (!payload || !payload.exp) return 0;

    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    const remainingMs = expirationTime - currentTime;

    return Math.max(0, Math.floor(remainingMs / 1000));
  }

  /**
   * Get token expiration date
   */
  static getTokenExpirationDate() {
    const payload = this.parseToken();
    if (!payload || !payload.exp) return null;

    return new Date(payload.exp * 1000);
  }

  /**
   * Get token info object
   */
  static getTokenInfo() {
    const payload = this.parseToken();
    if (!payload) return null;

    return {
      issuedAt: new Date(payload.iat * 1000),
      expiresAt: new Date(payload.exp * 1000),
      remainingSeconds: this.getTokenRemainingTime(),
      isExpired: this.isAccessTokenExpired(),
      payload: {
        employee_id: payload.employee_id,
        username: payload.username,
        email: payload.email,
        role: payload.role,
        department: payload.department
      }
    };
  }

  /**
   * Setup automatic logout on token expiration
   * Returns cleanup function
   */
  static setupAutoLogout(logoutCallback) {
    const checkInterval = setInterval(() => {
      if (!this.isAuthenticated()) {
        clearInterval(checkInterval);
        return;
      }

      const remainingSeconds = this.getTokenRemainingTime();

      // Warn user 5 minutes before expiration
      if (remainingSeconds === 300) {
        console.warn('Token will expire in 5 minutes');
        window.dispatchEvent(new CustomEvent('tokenExpiringWarning', {
          detail: { remainingSeconds: 300 }
        }));
      }

      // Auto logout when token expires
      if (remainingSeconds <= 0) {
        clearInterval(checkInterval);
        logoutCallback('Your session has expired. Please login again.');
      }
    }, 60000); // Check every minute

    // Return cleanup function
    return () => clearInterval(checkInterval);
  }
}

export default TokenManager;
